import map from 'lodash/map.js';
import merge from 'lodash/merge.js';

import { handleResponseError } from '@/Helpers.js';
import Activity from '@/models/Activity.js';
import AttemptAuthorization from '@/models/AttemptAuthorization.js';
import axios from '@/plugins/axios.js';
import moment from '@/plugins/vue-moment.js';
import BaseActivities from '@/store/modules/base/BaseActivities.js';

export default merge(BaseActivities({ name: 'Activities' }), {
    actions: {
        async markComplete({ commit }, { id, uuid }) {
            try {
                const response = await axios.put(`/api/activities/${id}/mark-complete`, {}, { errorHandle: false });

                commit('EDIT', {
                    uuid,
                    field: 'completed',
                    value: true,
                });

                commit('EDIT', {
                    uuid,
                    field: 'marked_completed_at',
                    value: moment.utc().format('YYYY-MM-DD HH:mm:ss'),
                });

                // Also flip the Vuex-ORM Activity: ActivityStatus.isCompleted (which gates the
                // corporate footer "Next" button) reads Activity.completed from the ORM, not this
                // Options store — so without this the button would not appear until a full reload.
                Activity.update({ where: id, data: { completed: true } });

                // Optimistically update the sidebar menu so dependent activities unlock immediately
                commit('Menu/MARK_ACTIVITY_COMPLETED', { activityId: id, completed: true }, { root: true });

                return response;
            } catch (error) {
                handleResponseError(error);
                return Promise.reject();
            }
        },

        async completeActivity({ dispatch, commit, getters }, payload) {
            // USE VUEX-ORM TO UPDATE THE ACTIVITY
            const response = await axios.post('/api/activity-submissions', payload, { errorHandle: false });

            const attemptAuthorizations = response.data.data.attempt_authorizations || [];

            if (attemptAuthorizations.length) {
                await AttemptAuthorization.update({
                    where: attemptAuthorization => map(attemptAuthorizations, 'id').includes(attemptAuthorization.id),
                    data: { used_at: moment.utc('YYYY-MM-DD HH:mm:ss') },
                });
            }

            await dispatch(
                'ActivitySubmissions/fetch',
                {
                    activityId: payload.activity_id,
                    activityType: payload.activity_type,
                    createdBy: response.data.data.created_by,
                },
                { root: true }
            );

            commit('EDIT', {
                uuid: payload.uuid,
                field: 'completed',
                value: true,
            });

            // Mirror completion onto the Vuex-ORM Activity too. ActivityStatus.isCompleted reads
            // Activity.completed from the ORM (not this Options store), and that getter gates the
            // corporate footer "Next" button — so without this ORM write the button only appears
            // after a full page reload (the AC-4 QA rejection). FileSubmission did this in its own
            // component; doing it here covers every type that completes through completeActivity
            // (view/acknowledge information, slide deck, podcast, ebook, LTI, video).
            Activity.update({ where: Number(payload.activity_id), data: { completed: true } });

            const activity = getters.getByKey({
                keyName: 'id',
                key: Number(payload.activity_id),
            });
            const currentSubmissionCount = activity?.user_submission_count ?? 0;

            commit('EDIT', {
                uuid: payload.uuid,
                field: 'user_submission_count',
                value: currentSubmissionCount + 1,
            });

            // Optimistically reflect completion in the sidebar menu
            commit(
                'Menu/MARK_ACTIVITY_COMPLETED',
                { activityId: payload.activity_id, completed: true },
                { root: true }
            );
        },
    },
});
